import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';

import type { TcHedgeType } from '@/api/option/requestOptionRfs.ts';
import type { TCPremiumType } from '@/api/trade-capture/option/request.model.ts';
import type {
  BarrierErrors,
  CheckedOption,
  CheckOptionResponse,
  HedgeErrorLegs,
  LegErrors,
  StrategyErrorLegs,
  TCOptionHedgeValues,
  TCOptionLegValues,
  VanillaErrorLegs,
} from '@/api/trade-capture/option/response.model.ts';
import { parseStrike } from '@/App/tiles/Option/utils/strikeUtils.ts';
import { getPreviousValue, getTcValidation } from '@/common/trade-capture/tradeCaptureValidation.ts';
import type {
  FxOption,
  HedgeType,
  LegId,
  OptionBarrier,
  OptionBarrierErrors,
  OptionErrors,
  OptionHedge,
  OptionLegProperties,
  OptionProperties,
  OptionStrike,
  OptionTradeCapture,
  PremiumType,
} from '@/store/option/state.ts';
import type { TcValidation, TradeCaptureChecked, TradeCaptureError, TradeCaptureErrors } from '@/store/types.ts';
import { type CurrencyChoice, FIRST_CURRENCY, SECOND_CURRENCY } from '@/types/common.ts';
import type { ObjectWithNullToUndefined } from '@/utils/typescript.ts';

export function mapTcResponse(
  response: CheckOptionResponse,
  tileId: string,
  sessionId: string,
  option?: FxOption,
): OptionTcResponse {
  const tradeCaptureOption = response[tileId];
  if (isNotDefined(tradeCaptureOption)) {
    const tradeCaptureError: TradeCaptureError = {
      status: 'ERROR',
      error: 'SERVER_ERROR',
      // what is the good version here ??
      sessionId,
      version: -1,
    };
    return { type: 'error', tradeCapture: tradeCaptureError };
  }
  return mapCheckOption(tradeCaptureOption, tileId, sessionId, option);
}

export function mapCheckOption(
  tradeCaptureOption: CheckedOption,
  tileId: string,
  sessionId: string,
  option?: FxOption,
): OptionTcResponse {
  const changedFields = tradeCaptureOption.changedFields;

  const { hedgeLegs, nonHedgeLegs } = getTcLegs(tileId, tradeCaptureOption);
  const hedgeType = mapTcHedgeType(changedFields.hedgeType);
  const validation = mapTcErrors(nonHedgeLegs, hedgeLegs);

  const firstTcLeg = nonHedgeLegs[0]?.values;
  const legs = nonHedgeLegs.map(({ legId, values: leg }) => {
    // TODO: leg currency. It is not sent in the TC payload so we need to retrieve it from the state
    const stateLeg = option?.properties.legs.find((leg) => leg.legId === legId);
    const notionalCurrencyIndex = leg.negotiatedCurrency ?? stateLeg?.notionalCurrencyIndex ?? 1;

    // FIXME: solving with TC is super messy. In case we submitted the solving premium at the package level, as per TC recommandation (typed strategies),
    //  we do not have enough information in the response payload to know on which leg to do the solving for the RFS
    //  so we need to rely on the state to know how to map the TC response to the corresponding leg
    const isSolvingLeg = option?.properties.legs.some((leg) => leg.strike?.type === 'solving' && leg.legId === legId);
    const packageLevelValues = changedFields.legs?.[0]?.legs[0];
    const solvingAtPackageLevel =
      isSolvingLeg &&
      (packageLevelValues?.productName === 'RiskReversal' ||
        packageLevelValues?.productName === 'Straddle' ||
        packageLevelValues?.productName === 'Strangle')
        ? packageLevelValues
        : undefined;

    const validationLeg = validation.legs.find((validationLeg) => validationLeg.legId === legId);

    const legProductName =
      leg.productName === 'FxoTriggerOption' ? 'Trigger' : leg.productName === 'FxoBinaryOption' ? 'Binary' : 'Vanilla';

    return withoutNullish<OptionLegProperties>({
      legId: legId,
      strike: mapStrike(leg ?? getPreviousValue(validationLeg?.strike)),
      type: leg?.type,
      side: leg?.side,
      notional: notionalCurrencyIndex === 2 ? leg.amount2 : leg.amount1,
      notionalCurrencyIndex: leg.negotiatedCurrency,
      legProductName,
      deliveryDate: leg?.deliveryDate,
      deliveryDateTenor: leg?.deliveryDateTenor,

      expiryDate: leg?.expiryDate,
      expiryDateTenor: leg?.expiryDateTenor,

      premiumPaymentDate: leg?.premiumPaymentDate,
      premiumPaymentDateTenor: leg?.premiumPaymentDateTenor,

      barrier1: legProductName === 'Vanilla' ? null : mergeBarrier(leg.barrier1, stateLeg?.barrier1),
      barrier2: legProductName === 'Vanilla' ? null : mergeBarrier(leg.barrier2, stateLeg?.barrier2),

      markup: leg?.markupAmount,
      premiumBid: leg?.premiumPaymentAmountBid,
      premiumAsk: leg?.premiumPaymentAmountAsk,
      volatilityAsk: leg?.clientVolAsk,
      volatilityBid: leg?.clientVolBid,

      ...(isDefined(solvingAtPackageLevel)
        ? {
            premiumBid: solvingAtPackageLevel.premiumPaymentAmountBid,
            premiumAsk: solvingAtPackageLevel.premiumPaymentAmountAsk,
          }
        : {}),
    });
  });

  const properties: Partial<OptionProperties> = withoutNullish<OptionProperties>({
    currencyPair: changedFields.currencyPair,
    legs,
    hedgeType,

    premiumPaymentDate: changedFields.premiumPaymentDate,
    premiumPaymentDateTenor: changedFields.premiumPaymentDateTenor,

    markupCurrency: changedFields.markupCurrency,
    premiumType: mapTcPremium(changedFields?.premiumType),

    amountReference: changedFields.amountReference,
    isAmountReferenceOverridden: changedFields.isOverriddenAmountReference,
    amountReferenceCurrency: changedFields.amountReferenceCurrency,

    cutOffMarketPlace: firstTcLeg?.cutOffMarketPlace,
    possibleCutOffMarketPlaces: firstTcLeg?.possibleCutOffMarketPlaces,

    cashSettlementCurrency: changedFields?.cashSettlementCurrency,
    possibleCashSettlementCurrencies: changedFields?.possibleCashSettlementCurrencies,

    settlementType: changedFields?.settlementType,
    possibleSettlementTypes: changedFields?.possibleSettlementTypes,

    isCrossed: changedFields.isCrossed,
    crossCurrency: changedFields.crossCurrency ?? changedFields.possibleCrossCurrencies?.default,
    crossCurrenciesList: changedFields.possibleCrossCurrencies?.possible,

    fixingReference1: changedFields.fixingReference1,
    fixingReference1Default: changedFields.possibleFixingReferences1?.default,
    fixingReference1List: changedFields.possibleFixingReferences1?.possible,
    fixingReference2: changedFields.fixingReference2,
    fixingReference2Default: changedFields.possibleFixingReferences2?.default,
    fixingReference2List: changedFields.possibleFixingReferences2?.possible,

    hedges: mapTCHedge(hedgeLegs),
  });

  const tradeCapture: OptionTradeCapture = {
    status: 'CHECKED',
    sessionId,
    version: tradeCaptureOption.idVersion,

    isReadyToPrice: changedFields.isReadyToPrice ?? false,
    isPriceObsolete: changedFields.isPriceObsolete ?? false,
    isProductObsolete: changedFields.isProductObsolete ?? false,

    validation,
  };

  return { type: 'success', tradeCapture, properties };
}

function mapTcErrors(nonHedgeLegs: TcNonHedgeLegValues[], hedgeLegs: TcHedgeLegValues[]): OptionErrors {
  return {
    hedges: hedgeLegs.map(({ legId, errors, warnings }) => {
      return {
        legId,
        type: getTcValidation(errors?.type, warnings?.type),
        rate: getTcValidation(errors?.price, warnings?.price),
      };
    }),
    legs: nonHedgeLegs.map(({ legId, errors, warnings }) => {
      return {
        legId,
        strike: getTcValidation(errors?.strike, warnings?.strike),
        notional: getTcValidation(errors?.amount1, warnings?.amount1),
        deliveryDate: getTcValidation(errors?.deliveryDate, warnings?.deliveryDate),
        expiryDate: getTcValidation(errors?.expiryDate, warnings?.expiryDate),
        premiumPaymentDate: getTcValidation(errors?.premiumPaymentDate, warnings?.premiumPaymentDate),
        barrier1: getBarrierValidation(errors?.barrier1, warnings?.barrier1),
        barrier2: getBarrierValidation(errors?.barrier2, warnings?.barrier2),
      };
    }),
  };
}

function getBarrierValidation(
  errors: BarrierErrors | undefined,
  warnings: BarrierErrors | undefined,
): OptionBarrierErrors | undefined {
  const errorsFields = errors ?? {};
  const warningsFields = warnings ?? {};
  const fields: Record<string, TcValidation[]> = {};

  for (const field of new Set([...Object.keys(errorsFields), ...Object.keys(warningsFields)])) {
    const validation = getTcValidation(errorsFields[field], warningsFields[field]);
    if (isDefined(validation)) fields[field] = validation;
  }

  return Object.keys(fields).length > 0 ? fields : undefined;
}

function mapTcPremium(premiumType: TCPremiumType | null | undefined): PremiumType | null | undefined {
  if (isNotDefined(premiumType)) {
    return premiumType;
  }

  switch (premiumType) {
    case 'AmountCurrency1':
      return { premiumCategory: 'AMOUNT', premiumCurrency: 1 };
    case 'AmountCurrency2':
      return { premiumCategory: 'AMOUNT', premiumCurrency: 2 };
    case 'PercentageCurrency1':
      return { premiumCategory: 'PERCENT', premiumCurrency: 1 };
    case 'PercentageCurrency2':
      return { premiumCategory: 'PERCENT', premiumCurrency: 2 };
    case 'SmiledVolatility1':
      return { premiumCategory: 'VOLATILITY', premiumCurrency: 1 };
    case 'SmiledVolatility2':
      return { premiumCategory: 'VOLATILITY', premiumCurrency: 2 };
    case 'Pip12':
      return { premiumCategory: 'PPS', premiumCurrency: 1 };
    case 'Pip21':
      return { premiumCategory: 'PPS', premiumCurrency: 2 };
    default:
      assertUnreachable(premiumType, `Unknown premium type: ${premiumType}`);
  }
}

function mapStrike(leg: TcNonHedgeLeg): OptionStrike | undefined {
  return parseStrike(leg.strikeShortcut ?? leg.strikeInDelta ?? leg.strike?.toString() ?? '');
}

/**
 * Transforms an object to remove undefined values and convert null values to undefined.
 *
 * This is useful to transform TC responses where null values are used to indicate that a field is not set.
 * The object can also have undefined values because they are just missing from the response.
 * Those undefined values are filtered out.
 *
 * @param param
 */
function withoutNullish<T extends object>(param: { [K in keyof T]?: T[K] | null }): ObjectWithNullToUndefined<T> {
  return Object.fromEntries(
    Object.entries(param)
      .map(([key, value]) => {
        // convert null to undefined
        if (value === null) {
          return [key, undefined];
        }
        // filter undefined values
        if (value === undefined) {
          return undefined;
        }
        return [key, value];
      })
      .filter(isDefined),
  ) as ObjectWithNullToUndefined<T>;
}

export type OptionTcResponse =
  | {
      type: 'success';
      tradeCapture: TradeCaptureChecked<OptionProperties, OptionErrors>;
      properties: Partial<OptionProperties>;
    }
  | { type: 'error'; tradeCapture: TradeCaptureError };

type TcHedgeLeg = Partial<TCOptionHedgeValues>;
type TcNonHedgeLeg = Partial<TCOptionLegValues>;

type TcHedgeLegValues = {
  legId: string;
  values: TcHedgeLeg;
  errors?: TradeCaptureErrors<TCOptionHedgeValues>;
  warnings?: TradeCaptureErrors<TCOptionHedgeValues>;
};
type TcNonHedgeLegValues = {
  legId: LegId;
  values: TcNonHedgeLeg;
  errors?: LegErrors;
  warnings?: LegErrors;
};

function getTcLegs(
  optionId: string,
  tradeCaptureOption: CheckedOption,
): { hedgeLegs: TcHedgeLegValues[]; nonHedgeLegs: TcNonHedgeLegValues[] } {
  const changedFields = tradeCaptureOption.changedFields;
  const errors = tradeCaptureOption.errors;
  const warnings = tradeCaptureOption.warnings;

  const tcLegMap = changedFields.legs?.['0']?.legs ?? {};
  const errorsLegMap = errors?.legs?.['0']?.legs ?? {};
  const warningsLegMap = warnings?.legs?.['0']?.legs ?? {};

  const hedgeLegs: TcHedgeLegValues[] = [];
  const nonHedgeLegs: TcNonHedgeLegValues[] = [];

  const [stratKey = '0', strat] = Object.entries(tcLegMap)[0] ?? [];
  const stratErrors = (errorsLegMap as HedgeErrorLegs)[stratKey] as StrategyErrorLegs['0'] | undefined;
  const stratWarnings = (warningsLegMap as HedgeErrorLegs)[stratKey] as StrategyErrorLegs['0'] | undefined;

  if (strat?.productName === 'Straddle' || strat?.productName === 'Strangle' || strat?.productName === 'RiskReversal') {
    nonHedgeLegs.push({
      values: { ...strat.legs?.call },
      legId: `${optionId}/0`,
      errors: stratErrors?.legs?.call,
      warnings: stratWarnings?.legs?.call,
    });
    nonHedgeLegs.push({
      values: { ...strat.legs?.put },
      legId: `${optionId}/1`,
      errors: stratErrors?.legs?.put,
      warnings: stratWarnings?.legs?.put,
    });

    for (const [tcLegId, legProperties] of Object.entries(tcLegMap)) {
      if (isHedgeLeg(legProperties)) {
        hedgeLegs.push({
          values: { ...legProperties },
          legId: tcLegId,
          errors: (errorsLegMap as HedgeErrorLegs)[tcLegId],
          warnings: (warningsLegMap as HedgeErrorLegs)[tcLegId],
        });
      }
    }
  } else {
    for (const [tcLegId, legProperties] of Object.entries(tcLegMap)) {
      if (isHedgeLeg(legProperties)) {
        hedgeLegs.push({
          values: { ...legProperties },
          legId: tcLegId,
          errors: (errorsLegMap as HedgeErrorLegs)[tcLegId],
          warnings: (warningsLegMap as HedgeErrorLegs)[tcLegId],
        });
      } else {
        const legId: LegId = `${optionId}/${Number(tcLegId)}`;
        nonHedgeLegs.push({
          values: { ...legProperties },
          legId,
          errors: (errorsLegMap as VanillaErrorLegs)[tcLegId as `${number}`],
          warnings: (warningsLegMap as VanillaErrorLegs)[tcLegId as `${number}`],
        });
      }
    }
  }

  return {
    hedgeLegs,
    nonHedgeLegs,
  };
}

function mapTcHedgeType(hedgeType: TcHedgeType | undefined): HedgeType | undefined {
  if (hedgeType === 'None') {
    return 'Live';
  }
  return hedgeType;
}

function isHedgeLeg(leg: Partial<TCOptionLegValues | TCOptionHedgeValues>): leg is Partial<TCOptionHedgeValues> {
  return leg.productName === 'FxOptionHedge';
}

function mapTCHedge(legs: TcHedgeLegValues[]): OptionHedge[] {
  return legs
    .map(({ legId, values: leg }) => {
      if (leg.productName !== 'FxOptionHedge') {
        return undefined;
      }

      const hedge: OptionHedge = withoutNullish({
        legId: legId,
        rate: leg.price,
        maturityDate: leg.deliveryDate,
        notionalAmount: mapHedgeAmount(leg.amountInCcy1, leg.amountInCcy2),
        notionalCurrency: mapHedgeCurrency(leg.amountInCcy1, leg.amountInCcy2),
      });
      return hedge;
    })
    .filter(isDefined);
}

function mapHedgeAmount(hedgeAmountInCcy1: number | null | undefined, hedgeAmountInCcy2: number | null | undefined) {
  if (hedgeAmountInCcy1 === null && isNotDefined(hedgeAmountInCcy2)) {
    return null;
  }
  if (hedgeAmountInCcy2 === null && isNotDefined(hedgeAmountInCcy1)) {
    return null;
  }
  return hedgeAmountInCcy1 ?? hedgeAmountInCcy2 ?? undefined;
}

function mapHedgeCurrency(
  hedgeAmountInCcy1?: number | null,
  hedgeAmountInCcy2?: number | null,
): CurrencyChoice | undefined {
  if (isDefined(hedgeAmountInCcy1)) {
    return FIRST_CURRENCY;
  }

  if (isDefined(hedgeAmountInCcy2)) {
    return SECOND_CURRENCY;
  }

  return undefined;
}

function mergeBarrier(
  newBarrier: TcNonHedgeLeg['barrier1'],
  oldBarrier: OptionBarrier | undefined,
): OptionBarrier | null | undefined {
  if (newBarrier === null) return null;
  if (newBarrier === undefined) return undefined;
  return (isDefined(oldBarrier) ? { ...oldBarrier, ...newBarrier } : newBarrier) as OptionBarrier;
}
